import {Constants} from '@client/util/constants';
// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
import {createAccountRequestToLegacy} from '@client/services/transformers/account-transformers';
import {AccountRequestStatusId} from '@client/enums/accountRequestStatuses';
import {getUser} from '@simpleinvest/front-end-auth';
import get from 'lodash.get';
import Editable from './editable';

const STEPS = [
  {
    name: 'Get started',
    id: 'get-started',
  },
  {
    name: 'Select a goal',
    id: 'goal-select',
  },
  {
    name: 'Assess your risk',
    id: 'risk-tolerance',
  },
  {
    name: 'Asset allocation',
    id: 'asset-allocation',
  },
  {
    name: 'Contact Information',
    id: 'contact-information',
  },
  {
    name: 'Confirm information',
    id: 'confirmation',
  },
];

const checkMissingFields = (tp) => {
  const requiredFields = [
    'Provider_Info.EID',
    'Provider_Info.Post-tax_Balance.Rollover_IRA_Slip',
    'Provider_Info.Pre-tax_Balance.Rollover_IRA_Slip',
    'Provider_Info.Pre-tax_Balance.Balance|Provider_Info.Post-tax_Balance.Balance',
    'Federal_ID_Info.Federal_ID',
    'Federal_ID_Info.Federal_ID_Type',
    'Demographic_Info.First_Name',
    'Demographic_Info.Last_Name',
    'Demographic_Info.Date_Of_Birth',
    'Personal_Info.Spousal_Consent',
  ];

  // eslint-disable-next-line unicorn/no-array-reduce
  return requiredFields.reduce((missing, current) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow, unicorn/no-array-reduce
    const found = current.split('|').reduce((found, key) => {
      const val = get(tp, key.trim(), null);

      return found || val !== null;
    }, false);

    // eslint-disable-next-line no-negated-condition, unicorn/prefer-spread, unicorn/no-negated-condition
    return !found ? missing.concat([current]) : missing;
  }, []);
};

export default class Onboarding {
  constructor() {
    this.STEPS = STEPS;
    this.salary = null;
    this.currentStep = null;
    this.goalId = null;
    this.goalDateInfo = null;
    this.allocationSet = null;
    this.thirdPartyData = null;
    this.requestsSent = null;
    this.active = null;
    this.settings = null;
    this.type = null;
    this.amountToInvest = null;
    this.editable = new Editable();
    this.isOnboarding = null;
    this.custodianId = null;
    this.portfolioType = null;
    this.complianceStatus = null;
  }

  // eslint-disable-next-line class-methods-use-this
  getAllDeps(store, fetchThirdparty) {
    return new Promise((resolve) => {
      const promises = [
        // first tier deps
        store.dispatch('risk/getQuestions'),
        store.dispatch('account/requests/getIncomeIntervals'),
        store.dispatch('account/requests/getNetWorthIntervals'),
        store.dispatch('profile/getClientInfo'),
        store.dispatch('user/getInvestmentProfile'),
        store.dispatch('user/employment/get'),
      ];

      if (fetchThirdparty) {
        promises.push(store.dispatch('onboarding/getThirdPartyData', {onboardingType: store.state.onboarding.type}));
      }

      Promise.all(promises).then(resolve);
    });
  }

  updateThirdpartyData({dispatch, getters, rootState, state}) {
    const missingFields = checkMissingFields(state.thirdPartyData.PFG_Wrapper);

    if (missingFields.length > 0) {
      throw missingFields;
    }

    // look over our thirdparty data and update db values appropriately
    if (!getters.isJsonValid) {
      return;
    }

    if (!this.isContactInfoSet(rootState)) {
      this.populateContactInfo(state, dispatch, rootState);
    }

    if (state.salary === null) {
      this.setAnnualIncome(state, dispatch, rootState);
    }
  }

  // eslint-disable-next-line class-methods-use-this, no-unused-vars, require-await
  async populateContactInfo(state, dispatch) {
    const payload = {
      user: {},
      mailing: {},
    };

    if (state.thirdPartyData.PFG_Wrapper.Contact_Address.Address_1) {
      payload.user = {
        country: Constants.DEFAULT_COUNTRY,
        address1: state.thirdPartyData.PFG_Wrapper.Contact_Address.Address_1,
        address2: state.thirdPartyData.PFG_Wrapper.Contact_Address.Address_2,
        city: state.thirdPartyData.PFG_Wrapper.Contact_Address.City,
        state: state.thirdPartyData.PFG_Wrapper.Contact_Address.State,
        zip: state.thirdPartyData.PFG_Wrapper.Contact_Address.Zip
          ? state.thirdPartyData.PFG_Wrapper.Contact_Address.Zip.slice(0, 5)
          : state.thirdPartyData.PFG_Wrapper.Contact_Address.Zip,
      };
    }

    if (state.thirdPartyData.PFG_Wrapper.Demographic_Info.Email !== null) {
      // set our email
      Object.assign(payload.user, {
        primary_email: state.thirdPartyData.PFG_Wrapper.Demographic_Info.Email,
      });
    }

    if (state.thirdPartyData.PFG_Wrapper.Mailing_Address.Address_1 !== null) {
      // add a mailing address
      Object.assign(payload.mailing, {
        street_address: state.thirdPartyData.PFG_Wrapper.Mailing_Address.Address_1,
        address2: state.thirdPartyData.PFG_Wrapper.Mailing_Address.Address_2,
        city: state.thirdPartyData.PFG_Wrapper.Mailing_Address.City,
        state: state.thirdPartyData.PFG_Wrapper.Mailing_Address.State,
        zipcode: state.thirdPartyData.PFG_Wrapper.Mailing_Address.Zip
          ? state.thirdPartyData.PFG_Wrapper.Mailing_Address.Zip.slice(0, 5)
          : state.thirdPartyData.PFG_Wrapper.Mailing_Address.Zip,
      });
      payload.user.sameMailing = false;
    }

    payload.user.primary_phone = state.thirdPartyData.PFG_Wrapper.Demographic_Info.Phone_Number;
    dispatch('user/setFullUser', payload, {root: true});
  }

  // eslint-disable-next-line class-methods-use-this
  isContactInfoSet(rootState) {
    return rootState.user.current.address1 !== null;
  }

  // eslint-disable-next-line class-methods-use-this, require-await
  async setAnnualIncome(state, dispatch) {
    if (state.thirdPartyData.PFG_Wrapper.Personal_Info.Annual_Income !== null) {
      dispatch('onboarding/setSalary', state.thirdPartyData.PFG_Wrapper.Personal_Info.Annual_Income, {root: true});
    }
  }

  // eslint-disable-next-line class-methods-use-this, require-await
  async prepopulateMaritalStatus(state, dispatch, rootState) {
    if (
      rootState.user.current.marital_status === null &&
      state.thirdPartyData &&
      // eslint-disable-next-line unicorn/explicit-length-check
      state.thirdPartyData.length &&
      state.thirdPartyData.PFG_Wrapper.Personal_Info.Marital_Status !== null
    ) {
      dispatch('user/setMaritalStatus', state.thirdPartyData.PFG_Wrapper.Personal_Info.Marital_Status, {root: true});
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async initAccountRequest(selectedAccount, custodian, dispatch) {
    const {userId} = await getUser();

    const accountRequest = await accountRequestService.updateAccountRequest({
      clUserId: Number(userId),
      custodian,
      accountType: selectedAccount.account_type,
      cdoInitiated: true,
      accountStatus: AccountRequestStatusId.FinishOwnerInformation,
    });

    return dispatch('account/requests/add', createAccountRequestToLegacy(accountRequest, selectedAccount), {root: true});
  }
}
